// API Service for Minted Marketplace
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Helper function to make API requests with timeout and cache busting
const apiRequest = async (endpoint, options = {}, timeoutMs = 15000) => {
  // Add cache-busting query parameter
  const cacheBuster = `_t=${Date.now()}`;
  const separator = endpoint.includes('?') ? '&' : '?';
  const url = `${API_BASE}${endpoint}${separator}${cacheBuster}`;
  
  const defaultHeaders = {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
  };

  // Add auth token if available
  const token = localStorage.getItem('mintedToken');
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
    cache: 'no-store',
  };

  // Add timeout using AbortController
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, { ...config, signal: controller.signal });
    clearTimeout(timeoutId);
    
    // Handle non-JSON responses gracefully
    const contentType = response.headers.get('content-type');
    
    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json().catch(() => ({}));
        errorMessage = errorData.detail || errorMessage;
      }
      throw new Error(errorMessage);
    }
    
    // Only parse JSON if response has JSON content
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }
    
    // Return empty object for non-JSON responses
    return {};
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      console.error(`API Timeout [${endpoint}]: Request took longer than ${timeoutMs}ms`);
      throw new Error('Request timed out. Please check your connection and try again.');
    } else {
      console.error(`API Error [${endpoint}]:`, error);
    }
    throw error;
  }
};

// ==================== AUTH ====================

export const authAPI = {
  register: async (userData) => {
    return apiRequest('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  login: async (email, password) => {
    return apiRequest('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  getUser: async (userId) => {
    return apiRequest(`/api/users/${userId}`);
  },

  updateUser: async (userId, updates) => {
    return apiRequest(`/api/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },
};

// ==================== PRODUCTS ====================

export const productsAPI = {
  getAll: async (category = null, limit = 500) => {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (limit) params.append('limit', limit);
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiRequest(`/api/products${query}`);
  },

  getById: async (productId) => {
    return apiRequest(`/api/products/${productId}`);
  },

  create: async (productData, sellerId) => {
    // 2-minute timeout for product creation (handles large images on slow connections)
    return apiRequest(`/api/products?seller_id=${sellerId}`, {
      method: 'POST',
      body: JSON.stringify(productData),
    }, 120000);
  },

  update: async (productId, updates) => {
    return apiRequest(`/api/products/${productId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  delete: async (productId) => {
    return apiRequest(`/api/products/${productId}`, {
      method: 'DELETE',
    });
  },

  getByCategory: async (category) => {
    return apiRequest(`/api/products?category=${encodeURIComponent(category)}`);
  },

  getBySeller: async (sellerId) => {
    return apiRequest(`/api/products/seller/${sellerId}`);
  },

  like: async (productId) => {
    return apiRequest(`/api/products/${productId}/like`, {
      method: 'POST',
    });
  },

  analyzeImage: async (imageBase64) => {
    return apiRequest('/api/products/analyze-image', {
      method: 'POST',
      body: JSON.stringify({ image_base64: imageBase64 }),
    });
  },
};

// ==================== CATEGORIES ====================

export const categoriesAPI = {
  getAll: async () => {
    return apiRequest('/api/categories');
  },
};

// ==================== ORDERS ====================

export const ordersAPI = {
  create: async (orderData, buyerId) => {
    return apiRequest(`/api/orders?buyer_id=${buyerId}`, {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
  },

  getByUser: async (userId, role = 'all') => {
    return apiRequest(`/api/orders/user/${userId}?role=${role}`);
  },

  addTracking: async (orderId, trackingNumber, trackingUrl = '', shippingMethod = 'Australia Post') => {
    return apiRequest(`/api/orders/${orderId}/tracking`, {
      method: 'POST',
      body: JSON.stringify({
        tracking_number: trackingNumber,
        tracking_url: trackingUrl,
        shipping_method: shippingMethod,
      }),
    });
  },

  confirmDelivery: async (orderId) => {
    return apiRequest(`/api/orders/${orderId}/confirm-delivery`, {
      method: 'POST',
    });
  },

  confirmPickup: async (orderId) => {
    return apiRequest(`/api/orders/${orderId}/confirm-pickup`, {
      method: 'POST',
    });
  },
};

// ==================== WALLET ====================

export const walletAPI = {
  get: async (userId) => {
    return apiRequest(`/api/wallet/${userId}`);
  },

  getTransactions: async (userId) => {
    return apiRequest(`/api/wallet/${userId}/transactions`);
  },

  requestWithdrawal: async (userId, amount, bankDetails) => {
    return apiRequest(`/api/wallet/${userId}/withdraw`, {
      method: 'POST',
      body: JSON.stringify({
        amount,
        bank_details: bankDetails,
      }),
    });
  },
};

// ==================== MESSAGES ====================

export const messagesAPI = {
  send: async (messageData, senderId) => {
    return apiRequest(`/api/messages?sender_id=${senderId}`, {
      method: 'POST',
      body: JSON.stringify(messageData),
    });
  },

  getConversations: async (userId) => {
    return apiRequest(`/api/messages/conversations/${userId}`);
  },

  getMessages: async (conversationId) => {
    return apiRequest(`/api/messages/conversation/${conversationId}`);
  },
};

// ==================== OFFERS ====================

export const offersAPI = {
  create: async (offerData, buyerId) => {
    return apiRequest(`/api/offers?buyer_id=${buyerId}`, {
      method: 'POST',
      body: JSON.stringify(offerData),
    });
  },

  getByUser: async (userId, role = 'buyer') => {
    return apiRequest(`/api/offers/user/${userId}?role=${role}`);
  },

  respond: async (offerId, status) => {
    return apiRequest(`/api/offers/${offerId}/respond?status=${status}`, {
      method: 'POST',
    });
  },
};

// ==================== NOTIFICATIONS ====================

export const notificationsAPI = {
  getAll: async (userId) => {
    return apiRequest(`/api/notifications/${userId}`);
  },

  markRead: async (notificationId) => {
    return apiRequest(`/api/notifications/${notificationId}/read`, {
      method: 'POST',
    });
  },

  markAllRead: async (userId) => {
    return apiRequest(`/api/notifications/${userId}/read-all`, {
      method: 'POST',
    });
  },
};

// ==================== STRIPE / CHECKOUT ====================

export const checkoutAPI = {
  createSession: async (productId, shippingMethod, buyerId) => {
    return apiRequest(`/api/checkout/create-session?buyer_id=${buyerId}`, {
      method: 'POST',
      body: JSON.stringify({
        product_id: productId,
        shipping_method: shippingMethod,
      }),
    });
  },
};

// Export all APIs
export default {
  auth: authAPI,
  products: productsAPI,
  categories: categoriesAPI,
  orders: ordersAPI,
  wallet: walletAPI,
  messages: messagesAPI,
  offers: offersAPI,
  notifications: notificationsAPI,
  checkout: checkoutAPI,
};
